import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpBorService from "@plannel/services/fp/fp-bor-service";
import RoutePopup from "@plannel/pages/factory-plan/common/popup/RoutePopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { RemoveButton } from "@plannel/components/ActionIconButton";
import Button from "@mui/material/Button";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// TODO(MIG-T2): [bor/BorSetTab] 체크바 그룹 병합(mergeRule) 미이관.
//   old: routeBorGrid.checkBar / borSetGrid.checkBar.mergeRule = "value['borSetCd']" — 같은
//     borSetCd(또는 checkBar 그룹)에 속한 행 하나를 체크하면 그룹 전체가 같이 체크됨.
//   사유: AG Grid Community 체크박스 선택은 행 단위라 그룹 자동 체크가 기본 제공되지 않음 —
//     1차 목표(그룹 저장/삭제 자체는 개별 행 체크로 동일하게 동작)엔 불필요.
const audit = {
  headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
  children: [
    { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
    { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
    { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
    { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
  ]
};

/**
 * 동시 사용 자원(BorSet) 탭 — old BorSetTab.jsx 대응. 상단 그리드(전체 BOR, 읽기전용)에서 행을
 * 클릭하면 그 공정(Route)에 속한 자원 목록(좌하단, 편집 가능 — 메인 자원 지정 + 체크된 행을 하나의
 * 세트로 저장)과 이미 저장된 자원 세트 목록(우하단, 삭제 전용)을 다시 조회한다(master-detail,
 * old allBorGridRowClick 대응). §11-1(항상 마운트)로 렌더링되므로 planScope/itemCdNm 변경 시
 * 전체 목록만 재조회하고, 선택된 공정 컨텍스트는 유지되지 않는다(재클릭 필요 — old도 탭 재진입 시
 * 초기화되는 것과 동일).
 *
 * props: t, viewName, planScope, searchFields
 */
const BorSetTab = ({ t, viewName, planScope, searchFields }) => {
  const allBorGridRef = useRef();
  const routeBorGridRef = useRef();
  const borSetGridRef = useRef();

  const [allBor, setAllBor] = useState([]);
  const [routeBor, setRouteBor] = useState([]);
  const [borSets, setBorSets] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [borSetDesc, setBorSetDesc] = useState("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [prodItemsPopup, setProdItemsPopup] = useState({ open: false, routeCd: null });

  const latestReqRef = useRef(0);

  const allBorGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_BOR"), viewName: `${viewName}_ALL` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);
  const routeBorGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_BOR_SET"), viewName: `${viewName}_ROUTE` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);
  const borSetGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_BOR_SET"), viewName: `${viewName}_SET` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성된다).
  const doesExternalFilterPass = (node) => {
    const { routeCdNm, resourceCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (routeCdNm && !(hit(r.routeCd, routeCdNm) || hit(r.routeNm, routeCdNm))) return false;
    if (resourceCdNm && !(hit(r.resourceCd, resourceCdNm) || hit(r.resourceNm, resourceCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => !!(searchFields.routeCdNm || searchFields.resourceCdNm);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    allBorGridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  // 공정 그룹 — 코드/명칭은 항상 표시, 생산 품목(routeProdItems)만 펼쳤을 때 표시(BorTab.js와
  // 동일 사유). 자원 그룹은 old도 expandable:false라 접기/펼침 없이 항상 표시.
  const allBorColumnDefs = [
    {
      headerName: "FP_ROUTE", groupId: "route", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", type: ["nonEditableColumn"], width: 150 },
        { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", type: ["nonEditableColumn"], width: 200 },
        // old routeProdItems button:"action" 대응 — BorTab.js와 동일하게 RoutePopup type="routeProdItems".
        { headerName: "FP_ROUTE_PROD_ITEM", field: "routeProdItems", colId: "routeProdItems", type: ["nonEditableColumn"], width: 200, columnGroupShow: "open",
          cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setProdItemsPopup({ open: true, routeCd: node.data?.routeCd }) } }
      ]
    },
    {
      headerName: "FP_RESOURCE", groupId: "resource", marryChildren: true, openByDefault: true,
      children: [
        { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", type: ["nonEditableColumn"], width: 150 },
        { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 200 }
      ]
    },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", type: ["nonEditableColumn"], width: 130 },
    audit
  ];

  const routeBorColumnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 200 },
    { headerName: "FP_DESC_TXT", field: "resourceDescTxt", colId: "resourceDescTxt", type: ["nonEditableColumn"], width: 130 },
    { headerName: "FP_RESOURCE_TP_CD", field: "isMainResource", colId: "isMainResource", type: ["booleanColumn", "centerAligned"], width: 110 }
  ];

  const borSetColumnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_BOR_SET_CD", field: "borSetCd", colId: "borSetCd", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_DESC_TXT", field: "mstDescTxt", colId: "mstDescTxt", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 200 },
    // old resourceTpCd는 dataType:"boolean"(체크박스) — 서버가 내려주는 'M'(주 자원)/'S'(보조)
    // 문자열을 old도 로드 직후 `data.resourceTpCd = data.resourceTpCd === 'M'`으로 boolean 변환해서
    // 넣었다(BorSetTab.jsx:309). new도 동일하게 조회 시점에 변환하고(retrieveBorSets) 여기서는
    // booleanColumn(§0-4)으로 체크박스 렌더링만 한다. 읽기 전용 그리드라 체크는 표시용이다.
    { headerName: "FP_RESOURCE_TP_CD", field: "resourceTpCd", colId: "resourceTpCd", type: ["booleanColumn", "centerAligned", "nonEditableColumn"], width: 110 }
  ];

  const retrieveAllBor = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    allBorGridRef.current?.api?.setGridOption("loading", true);

    fpBorService.getBors({ planScope, itemCdNm: searchFields.itemCdNm, isTool: false })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        setAllBor(isEmpty(response.data) ? [] : response.data);
      })
      .catch(e => debug(e))
      .finally(() => {
        if (reqId === latestReqRef.current) {
          allBorGridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const retrieveRouteBor = (routeCd) => {
    if (!planScope || !routeCd) return;
    routeBorGridRef.current?.api?.setGridOption("loading", true);
    fpBorService.getBors({ planScope, routeCd, isTool: false })
      .then(response => setRouteBor(isEmpty(response.data) ? [] : response.data))
      .catch(e => debug(e))
      .finally(() => {
        routeBorGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveBorSets = (routeCd) => {
    if (!planScope || !routeCd) return;
    borSetGridRef.current?.api?.setGridOption("loading", true);
    fpBorService.getBorSets({ planScope, routeCd })
      // resourceTpCd는 서버가 'M'(주 자원)/'S'(보조) 문자열로 내려주는데 그리드는 체크박스
      // (booleanColumn)로 표시한다 — old도 로드 직후 동일하게 변환했다(BorSetTab.jsx:309).
      .then(response => setBorSets(
        isEmpty(response.data) ? [] : response.data.map(row => ({ ...row, resourceTpCd: row.resourceTpCd === "M" }))
      ))
      .catch(e => debug(e))
      .finally(() => {
        borSetGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // GridUtils.setColumnDefs를 거쳐야 헤더(headerName)가 i18n 번역된다 — columnDefs를 그리드에
  // prop으로 직접 넘기면 raw 키 문자열("FP_ROUTE_CD" 등)이 그대로 노출된다.
  const onAllBorGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: allBorColumnDefs, viewName: `${viewName}_ALL`, initState: true });
    retrieveAllBor();
  };

  const onRouteBorGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: routeBorColumnDefs, viewName: `${viewName}_ROUTE`, initState: true });
  };

  const onBorSetGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: borSetColumnDefs, viewName: `${viewName}_SET`, initState: true });
  };

  const onAllBorRowClicked = (e) => {
    if (!e.data) return;
    const { routeCd, routeNm } = e.data;
    setSelectedRoute({ routeCd, routeNm });
    setBorSetDesc("");
    retrieveRouteBor(routeCd);
    retrieveBorSets(routeCd);
  };

  // isMainResource는 old 라디오형 단일 선택 — true로 바뀐 행 외 나머지는 자동으로 false.
  const onRouteBorCellValueChanged = (e) => {
    if (e.colDef.colId !== "isMainResource" || !e.newValue) return;
    e.api.forEachNode(node => {
      if (node.id !== e.node.id && node.data?.isMainResource) {
        node.setDataValue("isMainResource", false);
      }
    });
  };

  const saveBorSet = () => {
    routeBorGridRef.current.api.stopEditing();
    const checkedRows = routeBorGridRef.current.api.getSelectedRows();

    if (checkedRows.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_RESOURCE_ROW_SELECT") });
      return;
    }
    if (!checkedRows.some(row => row.isMainResource)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_MAIN_RESOURCE_SELECT") });
      return;
    }
    if (!borSetDesc.trim()) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_BOR_SET_DESC_TXT") });
      return;
    }

    const changeRowData = checkedRows.map(row => ({ ...row, borSetMstDescTxt: borSetDesc }));

    fpBorService.saveBorSets(changeRowData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => {
        setBorSetDesc("");
        retrieveRouteBor(selectedRoute.routeCd);
        retrieveBorSets(selectedRoute.routeCd);
      });
  };

  const removeBorSet = () => {
    const selectedRows = borSetGridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpBorService.removeBorSets(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => {
          retrieveBorSets(selectedRoute.routeCd);
          retrieveRouteBor(selectedRoute.routeCd);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "DELETE") removeBorSet();
  };

  // 화면 내부 탭(§11-3): planScope 또는 itemCdNm 변경 시에만 상단(전체 BOR) 그리드 재조회.
  // 하단 두 그리드는 상단 그리드 행 클릭(master-detail)으로만 갱신된다.
  useEffect(() => {
    if (!allBorGridRef.current?.api) return;
    retrieveAllBor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields.itemCdNm]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", pt: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, mb: 1 }}>
        <Box id="fp-bor-set-all-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
          <AgGridReact
            ref={allBorGridRef}
            rowData={allBor}
            {...allBorGridMemo}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            onRowClicked={onAllBorRowClicked}
            onGridReady={onAllBorGridReady}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", flex: 1.2, gap: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {/* 우측 헤더줄(RemoveButton만)과 높이가 달라 그 아래 두 그리드가 수직으로 안 맞던 문제 —
              양쪽 헤더줄에 minHeight+alignItems:center로 높이를 맞춘다. */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 40 }}>
            {/* 섹션 제목(굵기 500) + 선택 공정 정보(구분선 뒤 보조색) — FP 그리드 섹션 라벨 공통
                형태(fp-master-screen-guide.md §15-1, 기준 화면 master/Bom.js). */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{t("FP_ROUTE")}</Box>
              {/* 구분선은 표시할 값이 실제로 있을 때만 — 객체 존재 여부로 판정하면 값이 비었을 때 선만 남는다. */}
              {selectedRoute?.routeCd && (
                <>
                  <Divider orientation="vertical" sx={{ height: 16, alignSelf: "center", borderColor: grey[400] }} />
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {`${selectedRoute.routeCd} (${selectedRoute.routeNm})`}
                  </Box>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField label={t("FP_BOR_SET_DESC_TXT")} size="small" value={borSetDesc} onChange={e => setBorSetDesc(e.target.value)} />
              <Button variant="contained" size="small" onClick={saveBorSet}>{t("save")}</Button>
            </Box>
          </Box>
          <Box id="fp-bor-set-route-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={routeBorGridRef}
              rowData={routeBor}
              {...routeBorGridMemo}
              rowSelection={{ ...routeBorGridMemo.rowSelection, headerCheckbox: true, checkboxes: true }}
              onCellValueChanged={onRouteBorCellValueChanged}
              onGridReady={onRouteBorGridReady}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 40 }}>
            <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{t("FP_BOR_SET")}</Box>
            <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          </Box>
          <Box id="fp-bor-set-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={borSetGridRef}
              rowData={borSets}
              {...borSetGridMemo}
              rowSelection={{ ...borSetGridMemo.rowSelection, headerCheckbox: true, checkboxes: true }}
              onGridReady={onBorSetGridReady}
            />
          </Box>
        </Box>
      </Box>
      <RoutePopup
        open={prodItemsPopup.open}
        onClose={() => setProdItemsPopup({ open: false, routeCd: null })}
        planScope={planScope}
        type="routeProdItems"
        routeCd={prodItemsPopup.routeCd}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Box>
  );
};

export default withTranslation()(BorSetTab);
